import { clamp01 } from '../core/math';
import { Ring } from '../core/ring';
import type { Side } from '../pose/landmarks';
import type { DefenseEvent, FootworkEvent, GuardState, PunchEvent } from '../recog/events';

/**
 * DELAYED PERCEPTION + ONLINE OPPONENT MODELLING
 * ==============================================
 *
 * The AI never reads the live world. Everything the player does enters a delay
 * queue and only becomes visible after `reactionMs`. This single constraint is
 * what makes the opponent feel human: it *cannot* block a punch it has not yet
 * perceived, so a fast, disguised shot genuinely beats it, at every difficulty.
 *
 * A pure delay queue, though, caps the opponent at "slow human". Real fighters
 * close that gap by *reading* you, not by reacting faster. So alongside the queue
 * we run a small online model of the player — a decayed first-order Markov chain
 * over punch numbers plus a habit table — and let high-difficulty AI act on its
 * predictions before the evidence arrives.
 *
 * That has an important property the raw-latency approach lacks: it is beatable
 * by *changing your behaviour*. Vary your combinations and the model's confidence
 * collapses, the AI stops pre-empting, and it drops back to honest reaction time.
 * The player can feel themselves outthinking it. That is the whole game.
 */

export type Percept =
  | { kind: 'punch-start'; t: number; side: Side }
  | { kind: 'punch'; t: number; punch: PunchEvent }
  | { kind: 'defense'; t: number; ev: DefenseEvent }
  | { kind: 'footwork'; t: number; ev: FootworkEvent }
  | { kind: 'guard'; t: number; guard: GuardState; gap: number; lateral: number };

interface Queued {
  releaseAt: number;
  percept: Percept;
}

const N = 9; // 0 = "no punch / reset", 1..8 = boxing numbers

/** Decayed first-order Markov chain over the player's punch numbers. */
export class PlayerModel {
  private counts = new Float32Array(N * N);
  private lastNumber = 0;
  private intervals = new Ring<number>(24);
  private lastPunchT = 0;

  /** Per-punch-number tally of which hand the player drops while throwing it. */
  private dropLeft = new Float32Array(N);
  private dropRight = new Float32Array(N);
  private dropTotal = new Float32Array(N);

  /** How often the player follows a punch with head movement rather than more punches. */
  private slipAfter = new Float32Array(N);

  observePunch(p: PunchEvent, guard: GuardState): void {
    const n = p.number;
    // Decay keeps the model responsive to a player who changes tactics mid-round,
    // which is exactly the behaviour we want to reward.
    for (let i = 0; i < this.counts.length; i++) this.counts[i] *= 0.985;
    this.counts[this.lastNumber * N + n] += 1;

    if (this.lastPunchT > 0) {
      const gap = p.tStart - this.lastPunchT;
      if (gap > 60 && gap < 1500) this.intervals.push(gap);
    }
    this.lastPunchT = p.tPeak;
    this.lastNumber = n;

    this.dropTotal[n] += 1;
    if (guard.left < 0.45) this.dropLeft[n] += 1;
    if (guard.right < 0.45) this.dropRight[n] += 1;
  }

  observeDefense(): void {
    if (this.lastNumber > 0) this.slipAfter[this.lastNumber] += 1;
  }

  /** Marks the end of a combination. */
  reset(): void {
    this.lastNumber = 0;
    this.lastPunchT = 0;
  }

  /** Most likely next punch number and the confidence in it. */
  predictNext(): { number: number; p: number } {
    const row = this.lastNumber * N;
    let total = 0;
    for (let i = 0; i < N; i++) total += this.counts[row + i];
    if (total < 4) return { number: 0, p: 0 };
    let best = 0;
    let bestC = -1;
    for (let i = 1; i < N; i++) {
      if (this.counts[row + i] > bestC) {
        bestC = this.counts[row + i];
        best = i;
      }
    }
    // Confidence is the observed probability, discounted by how little evidence
    // we have. Ten samples of a 90% pattern should not read the same as three.
    const p = (bestC / total) * clamp01(total / 12);
    return { number: best, p };
  }

  /** Expected ms until the next punch of a combination, or 0 if unknown. */
  expectedIntervalMs(): number {
    if (this.intervals.size < 3) return 0;
    let s = 0;
    let n = 0;
    for (const v of this.intervals.recent(12)) {
      s += v;
      n++;
    }
    return s / n;
  }

  /** Which hand the player habitually drops when throwing `number`, if any. */
  openingFor(number: number): { side: Side | null; p: number } {
    const t = this.dropTotal[number];
    if (t < 3) return { side: null, p: 0 };
    const l = this.dropLeft[number] / t;
    const r = this.dropRight[number] / t;
    if (Math.max(l, r) < 0.4) return { side: null, p: 0 };
    return l > r ? { side: 'left', p: l } : { side: 'right', p: r };
  }

  /** 0..1 — how predictable the player currently is overall. Shown in the HUD. */
  get predictability(): number {
    let total = 0;
    let maxRowShare = 0;
    let rows = 0;
    for (let a = 0; a < N; a++) {
      let rowTotal = 0;
      let rowMax = 0;
      for (let b = 0; b < N; b++) {
        const c = this.counts[a * N + b];
        rowTotal += c;
        rowMax = Math.max(rowMax, c);
      }
      if (rowTotal >= 3) {
        maxRowShare += rowMax / rowTotal;
        rows++;
      }
      total += rowTotal;
    }
    if (rows === 0 || total < 6) return 0;
    return clamp01((maxRowShare / rows - 1 / 8) / (1 - 1 / 8));
  }

  clear(): void {
    this.counts.fill(0);
    this.dropLeft.fill(0);
    this.dropRight.fill(0);
    this.dropTotal.fill(0);
    this.slipAfter.fill(0);
    this.intervals.clear();
    this.lastNumber = 0;
    this.lastPunchT = 0;
  }
}

/** The AI's (lagged, partial) view of the world. */
export interface Belief {
  t: number;
  /** Last perceived guard state. */
  guard: GuardState | null;
  gap: number;
  lateral: number;
  /** ms since the player was last perceived to start a punch. */
  sinceOnset: number;
  /**
   * Which hand the player was last perceived to start punching with.
   *
   * This is the single most valuable pre-impact cue there is: you cannot tell a
   * jab from a hook while it is in the air, but you can absolutely see which
   * glove left the guard. The recogniser has always reported it; the blackboard
   * used to discard it and fall back to guessing, which made the opponent's head
   * movement look disconnected from what the player was actually doing.
   */
  onsetSide: Side | null;
  /** ms since the player was last perceived to finish a punch. */
  sinceRecovery: number;
  /** Perceived punches in the current player combination. */
  comboLength: number;
  /** Player appears committed (mid-punch) right now, as far as we can tell. */
  playerCommitted: boolean;
  /** Player is moving away. */
  playerRetreating: boolean;
  /** The predictor's current call. */
  prediction: { number: number; p: number };
  /** Habitual opening associated with the predicted punch. */
  opening: { side: Side | null; p: number };
}

export class Blackboard {
  private queue: Queued[] = [];
  readonly model = new PlayerModel();
  readonly belief: Belief = {
    t: 0,
    guard: null,
    gap: 1.3,
    lateral: 0,
    sinceOnset: 1e6,
    onsetSide: null,
    sinceRecovery: 1e6,
    comboLength: 0,
    playerCommitted: false,
    playerRetreating: false,
    prediction: { number: 0, p: 0 },
    opening: { side: null, p: 0 },
  };

  private lastOnsetAt = -1e6;
  private lastPunchAt = -1e6;
  private comboOpenedAt = -1e6;

  constructor(
    private reactionMs: number,
    private jitterMs: number,
    private rng: () => number,
  ) {}

  setReaction(reactionMs: number, jitterMs: number): void {
    this.reactionMs = reactionMs;
    this.jitterMs = jitterMs;
  }

  /** Push a percept. It becomes visible after the reaction delay. */
  push(p: Percept, now: number): void {
    // Jitter is asymmetric: humans are occasionally very late but never early.
    const jitter = this.jitterMs * this.rng();
    this.queue.push({ releaseAt: now + this.reactionMs + jitter, percept: p });
  }

  /** Guard snapshots are pushed continuously but only the newest matters. */
  pushGuard(guard: GuardState, gap: number, lateral: number, now: number): void {
    this.push({ kind: 'guard', t: now, guard: { ...guard }, gap, lateral }, now);
  }

  update(now: number): void {
    const b = this.belief;
    b.t = now;
    // The queue is naturally ordered because reaction delay is monotone up to
    // jitter; a stable partition keeps that true without a full sort per frame.
    let i = 0;
    while (i < this.queue.length) {
      const q = this.queue[i];
      if (q.releaseAt > now) {
        i++;
        continue;
      }
      this.apply(q.percept, now);
      this.queue.splice(i, 1);
    }

    b.sinceOnset = now - this.lastOnsetAt;
    b.sinceRecovery = now - this.lastPunchAt;
    // "Committed" means: we perceived an onset recently enough that the hand is
    // probably still out. Punch cycles are ~250-400 ms.
    b.playerCommitted = b.sinceOnset < 320;
    if (now - this.comboOpenedAt > 900) b.comboLength = 0;
    b.prediction = this.model.predictNext();
    b.opening = this.model.openingFor(b.prediction.number || 1);
  }

  private apply(p: Percept, now: number): void {
    const b = this.belief;
    switch (p.kind) {
      case 'punch-start':
        this.lastOnsetAt = now;
        b.onsetSide = p.side;
        break;
      case 'punch':
        this.lastPunchAt = now;
        b.comboLength++;
        this.comboOpenedAt = now;
        this.model.observePunch(p.punch, b.guard ?? emptyGuard());
        break;
      case 'defense':
        this.model.observeDefense();
        break;
      case 'footwork':
        b.playerRetreating = p.ev.kind === 'step-out';
        break;
      case 'guard':
        b.guard = p.guard;
        b.gap = p.gap;
        b.lateral = p.lateral;
        break;
    }
  }

  clear(): void {
    this.queue.length = 0;
    this.model.clear();
    this.belief.comboLength = 0;
    this.lastOnsetAt = -1e6;
    this.lastPunchAt = -1e6;
  }
}

function emptyGuard(): GuardState {
  return {
    left: 1,
    right: 1,
    shell: 1,
    headOffX: 0,
    headOffY: 0,
    headOffZ: 0,
    evading: null,
    evasionDepth: 0,
    lastEvasionStartT: 0,
    lastEvasionPeakT: 0,
    lastEvasionKind: null,
    lastEvasionDepth: 0,
    lastEvasionOffX: 0,
  };
}
